var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '../../theme';
import { useMemoClasses } from '../../utils/hooks';
import { isElementInEventPath, areEqualShallow } from '../../utils/functions';
import DropdownWindow from './DropdownWindow';
var useStyles = createUseStyles({
    anchor: {
        display: 'inline-block',
    },
    anchorOverrides: function (theme) { return (__assign({}, theme.overrides.Dropdown.anchor)); },
}, { name: 'RUIDropdown' });
var DropdownAnchor = function (_a) {
    var baseNode = _a.baseNode, mode = _a.mode, index = _a.index, onIndexChange = _a.onIndexChange, onOpenToggle = _a.onOpenToggle, props = __rest(_a, ["baseNode", "mode", "index", "onIndexChange", "onOpenToggle"]);
    var changeOpenRef = useRef(null);
    var anchorRef = useRef(null);
    var windowRef = useRef(null);
    var _b = useState(null), rect = _b[0], setRect = _b[1]; // bounding rect of anchor to calculate window location
    var _c = useState(false), open = _c[0], setOpen = _c[1]; // open state of window
    var _d = useState(0), innerIndex = _d[0], setInnerIndex = _d[1]; // index of focused item on window
    var theme = useTheme();
    var styles = useStyles(theme);
    var changeOpen = function (value, e) {
        var _a;
        setOpen(value);
        // inform external components about open change
        var futureOpen = typeof value === 'function' ? value(open) : value;
        onOpenToggle && onOpenToggle(futureOpen, e);
        // if not closed due to outside click
        if (!futureOpen && !(e instanceof UIEvent)) {
            // bring focus back to base or one of its children
            var base = (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild;
            while (true) {
                base === null || base === void 0 ? void 0 : base.focus();
                if (document.activeElement !== base && base.firstElementChild)
                    base = base.firstElementChild;
                else
                    break;
            }
        }
    };
    // function is used on a callback so it must be kept updated
    changeOpenRef.current = changeOpen;
    // Enter to open, Enter & Tab to close
    // (Tab can't be used to open because its used to change focus between elements)
    var handleKeyDown = function (e) {
        if (mode.includes('keyboard')) {
            if (open && ['Enter', 'Tab'].includes(e.key)) {
                changeOpen(false, e);
                e.preventDefault();
            }
            else if (!open && e.key === 'Enter') {
                changeOpen(true, e);
            }
        }
    };
    // click on base/window to toggle open
    var handleClick = function (e) {
        if (mode.includes('click')) {
            changeOpen(function (i) { return !i; }, e);
        }
    };
    // Click outisde to close
    var callback = useCallback(function (e) {
        if (!isElementInEventPath([windowRef.current, anchorRef.current], e)) {
            changeOpenRef.current(false, e);
        }
    }, []);
    // Event listener to detect click outside dropdown
    useEffect(function () {
        if (mode.includes('click')) {
            if (open)
                window.addEventListener('click', callback);
            else
                window.removeEventListener('click', callback);
        }
        return function () { return window.removeEventListener('click', callback); };
    }, [open, mode]);
    // Hover in to open
    var handleMouseEnter = function (e) {
        if (mode.includes('hover'))
            changeOpen(true, e);
    };
    // Hover out to close
    var handleMouseLeave = function (e) {
        // no focus when closing with hover
        if (mode.includes('hover'))
            changeOpen(false, e);
    };
    // calculate rect when opened to send it to DropdownWindow
    useEffect(function () {
        var _a, _b;
        if (open) {
            var newRect = (_b = (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
            // set rect only if it is a new one
            if (!areEqualShallow(newRect, rect))
                setRect(newRect);
            // reset inner index in case it is used
            setInnerIndex(0);
        }
    }, [open]);
    // classes for anchor div
    var classes = useMemoClasses([styles.anchor, theme.overrides.Dropdown.anchor && styles.anchorOverrides, 'RUIDropdown-anchor'], [theme]);
    return (React.createElement("div", { className: classes, ref: anchorRef, onClick: handleClick, onKeyDown: handleKeyDown, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
        baseNode,
        React.createElement(DropdownWindow, __assign({ anchorRect: rect, open: open, mode: mode, index: index !== undefined ? index : innerIndex, onIndexChange: onIndexChange || setInnerIndex, windowRef: windowRef }, props))));
};
DropdownAnchor.defaultProps = {
    transitionDuration: 200,
    justify: 'left',
    position: 'bottom',
    mode: ['keyboard', 'click'],
};
export { DropdownAnchor as default, DropdownAnchor };
